import { useTranslation } from 'react-i18next';

import { ColumnProps } from 'antd/lib/table';
import { Dropdown, Button, Menu } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { DateFormatted, Icon, Permission, Tooltip } from 'Components';
import {
	useCreateColumns,
	ColumnFilters,
	UseCreateColumnsInterface
} from 'Components/Table/createColumns';
import { getAppRoutes } from 'Pages/App/App.constants';
import { GetSystemRolesRequest, SystemRole } from 'Services/Api/Roles/Types';
import { GetUsersRequest, PermissionCode } from 'Services/Api/Users/Types';
import { qsStringify, toPath } from 'Helpers/navigation';
import { getUserRoleLabel } from 'app-util';
import { generatePath, Link } from 'react-router-dom';
import './styles/Roles.module.scss';
import { usePermission } from 'Components/Permission/Permission.hooks';
import moment from 'moment';
import { APP_CONFIG } from 'app-config';

type FilterValues = GetSystemRolesRequest['filters'];
export type TableFilterValues = ColumnFilters<FilterValues>;

export interface RoleRowActions<T extends SystemRole> {
	onViewDetail?: (record: T) => void;
	onEdit?: (record: T) => void;
	onRestoreToDefaults?: (record: T) => void;
	onClone?: (record: T) => void;
	onDelete?: (record: T) => void;
	selectedTenant: number
}

export function useColumnProps<T extends SystemRole>(
	helpers?: ReturnType<UseCreateColumnsInterface<T, FilterValues>>,
	actions?: RoleRowActions<T>
) {
	const { t } = useTranslation();

	const columns: Array<ColumnProps<T>> = [
		{
			title: t('roles.columns.role'),
			key: 'roleName',
			dataIndex: 'roleName',
			width: 150,
			ellipsis: { showTitle: false },
			filteredValue: helpers?.filters.roleName,
			render: (_, record) => (
				<RoleEllipsisText text={getUserRoleLabel(record.roleName)} />
			)
		},
		{
			title: t('roles.columns.description'),
			key: 'description',
			dataIndex: 'description',
			width: 200,
			ellipsis: { showTitle: false },
			render: (value: string) => (
				<RoleEllipsisText text={value} />
			)
		},
		{
			title: t('roles.columns.type'),
			key: 'type',
			dataIndex: 'type',
			width: 150,
			  onHeaderCell: () => ({
				style: {
					textAlign: 'center'
				},
			}),
			render: (_, record) => (
				<p className={record.type === 'Primary' ? 'primary-role-batch' : 'secondary-role-batch'}><span>{record.type}</span></p>
			)
		},
		{
			title: t('roles.columns.usersCount'),
			key: 'usersCount',
			dataIndex: 'usersCount',
			width: 150,
			render: (value) => {
				return <>{value}</>;
			}
		},
		{
			title: t('roles.columns.lastUpdate'),
			key: 'updatedDate',
			dataIndex: 'updatedDate',
			width: 200,
			render: (value) => {
				return !!value ? moment(value).format(APP_CONFIG.MONTH_DATE_FORMAT) : ""
			}
		},
		{
			title: "",
			key: 'actions',
			dataIndex: 'actions',
			className: "action-btn",
			width: 40,
			render: (_, record) => (
				<ActionsButton record={record} actions={actions} />
			)
		}
	];
	return columns;
}

export function RoleEllipsisText({
	text,
	fallback = ''
}: {
	text?: string | null;
	fallback?: string;
}) {
	const display = text?.trim() ? text : fallback;
	if (!display) {
		return null;
	}

	return (
		<Tooltip
			title={text}
			placement="topLeft"
			enabled={Boolean(text?.trim())}
			overlayClassName="role-cell-tooltip"
		>
			<span className="role-ellipsis-text">{display}</span>
		</Tooltip>
	);
}

type ActionsButtonProps<T extends SystemRole> = {
	record: T;
	actions?: RoleRowActions<T>;
};

function ActionsButton<T extends SystemRole>({
	record,
	actions
}: ActionsButtonProps<T>) {
	const { t } = useTranslation();

	// Resolve permissions to plain booleans BEFORE building the menu items.
	// antd's <Menu> only clones/styles elements that are its direct children,
	// so wrapping <Menu.Item> inside a <Permission> render-prop component
	// hides it from that traversal and it loses its antd classes/props.
	const { getHasPermission } = usePermission();

	const canView = !!getHasPermission({ code: PermissionCode.ViewRoles }).permitted;
	const canEdit = record.type?.toLowerCase() === 'primary' ? !!getHasPermission({ code: PermissionCode.EditSystemRole }).permitted: !!getHasPermission({ code: PermissionCode.EDIT_SECONDARY_ROLE }).permitted;
	const canRestore = !!getHasPermission({ code: PermissionCode.RESTORE_PRIMARY_ROLE }).permitted;
	const canClone = !!actions?.selectedTenant && !!getHasPermission({ code: PermissionCode.CREATE_SECONDARY_ROLE }).permitted;
	const canDelete = !!getHasPermission({ code: PermissionCode.DELETE_SECONDARY_ROLE }).permitted;
	const showRestore =
		canRestore &&
		record.type?.toLowerCase() === 'primary' &&
		!!record?.isPrimaryRoleOverride;
	const showDelete = canDelete && record.type !== 'Primary';

	const menu = (
		<Menu className="or-list-selector">
			{canView && (
				<Menu.Item
					key="viewDetail"
					onClick={() => actions?.onViewDetail?.(record)}
				>
					{t('roles.actions.view')}
				</Menu.Item>
			)}
			{canEdit && (
				<Menu.Item key="edit" onClick={() => actions?.onEdit?.(record)}>
					{t('roles.actions.edit')}
				</Menu.Item>
			)}
			{showRestore && (
				<Menu.Item
					key="restoreToDefaults"
					onClick={() => actions?.onRestoreToDefaults?.(record)}
				>
					{t('roles.actions.restoreToDefaults')}
				</Menu.Item>
			)}
			{canClone && (
				<Menu.Item key="clone" onClick={() => actions?.onClone?.(record)}>
					{t('roles.actions.clone')}
				</Menu.Item>
			)}
			{showDelete && (
				<Menu.Item key="delete" onClick={() => actions?.onDelete?.(record)}>
					<span className="role-action-delete">
						{t('roles.actions.delete')}
					</span>
				</Menu.Item>
			)}
		</Menu>
	);

	return (
		<Dropdown className='role-action-button' overlay={menu} arrow={false} trigger={['click']} placement="bottomRight">
			<Button
				type="text"
				icon={<Icon type="optionIcon" />}
				onClick={(e) => e.stopPropagation()}
			/>
		</Dropdown>
	);
}

const useColumns = <T extends SystemRole>({
	params,
	actions
}: {
	params: GetSystemRolesRequest | undefined;
	actions?: RoleRowActions<T>;
}) => {
	const helpers = useCreateColumns<T, FilterValues>({
		filterValues: params?.filters || {},
		sortDesc: params?.sort
	});
	return useColumnProps(helpers, actions);
};

export default useColumns;